import React, { useEffect, useState } from 'react';

import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import ConfirmModal from 'src/components/shared/confirmModal/confirmModal';
import LoadingSpinnerWrapper from 'src/components/shared/loadingSpinner/loadingSpinnerWrapper';
import TopOptions from 'src/components/shared/topOptions/topOptions';
import ModificationService from 'src/services/modification/modificationService';
import { type Modification } from 'src/services/modification/types';
import { getTaskModificationConfigTableData } from 'src/store/modificationsSlice';
import { useAppDispatch } from 'src/store/store';

import JobModificationModal from './components/modificationModal';
import JobModificationTable from './components/modificationTable';

/**
 * Default component
 */
const JobModificationConfig = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isConfirmDeleteModalOpen, setIsConfirmDeleteModalOpen] = useState(false);
  const [modificationToDelete, setModificationToDelete] = useState<Modification | null>(null);
  const [selectedModification, setSelectedModification] = useState<Modification | null>(null);
  const [searchField, setSearchField] = useState('');

  // Note: this is getting called twice in dev because of <React.StrictMode/>
  useEffect(() => {
    dispatch(getTaskModificationConfigTableData())
      .unwrap()
      .finally(() => setIsLoading(false));
  }, []);

  const onSave = async (payload: Modification) => {
    const { name } = await ModificationService.createOrUpdateModifications(payload);
    setIsCreateModalOpen(false);
    setSearchField(name);
    setSelectedModification(null);
    await dispatch(getTaskModificationConfigTableData()).unwrap();
  };

  const deleteModification = async () => {
    await ModificationService.deleteModification(modificationToDelete!.id);
    await dispatch(getTaskModificationConfigTableData()).unwrap();
    setIsConfirmDeleteModalOpen(false);
    setModificationToDelete(null);
  };

  const onCancel = () => {
    setIsCreateModalOpen(false);
    setSelectedModification(null);
  };

  const onEditClick = (row: Modification) => {
    setSelectedModification(row);
    setIsCreateModalOpen(true);
  };

  const onDeleteClick = (row: Modification) => {
    setIsConfirmDeleteModalOpen(true);
    setModificationToDelete(row);
  };

  return (
    <div>
      <Row spacingInset="400 400 500 none">
        <Text alignment="center">Here you can view and modify the list of task modifications within RTW</Text>
      </Row>
      <LoadingSpinnerWrapper showSpinner={isLoading}>
        <TopOptions
          label="Modification"
          onOpen={() => setIsCreateModalOpen(true)}
          searchField={searchField}
          setSearchField={setSearchField}
        />
        <JobModificationTable onDelete={onDeleteClick} onEdit={(row) => onEditClick(row!)} searchField={searchField} />
      </LoadingSpinnerWrapper>
      <JobModificationModal
        selectedModification={selectedModification}
        isOpen={isCreateModalOpen}
        onSave={onSave}
        onCancel={onCancel}
      />
      <ConfirmModal
        open={isConfirmDeleteModalOpen}
        setOpen={setIsConfirmDeleteModalOpen}
        onConfirm={() => deleteModification()}
        modalTitle="Confirm Delete?"
        modalDescription={`Are you sure you want to delete ${modificationToDelete?.name}?`}
      />
    </div>
  );
};

// **** Export **** //

export default JobModificationConfig;
